import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "../icons/CloseIcon";
import PropTypes from "prop-types";
import SurveyPillQuestion from "../shared/SurveyPillQuestion";
import SurveyTextField from "./SurveyTextField";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getPreChatChoices } from "../../preChatChoices";

const SurveyAccordion = ({ control, spacingTop }) => {
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (event, isExpanded) => {
    const scrollPosition = window.scrollY;
    setExpanded(isExpanded);

    // Prevent the page from jumping up or down when the accordion changes
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  };

  const RotatableAddIcon = styled(CloseIcon)(({ rotate }) => ({
    transition: "transform 0.3s",
    transform: rotate ? "rotate(45deg)" : "rotate(0deg)",
  }));

  const { t } = useTranslation(["preChat", "common"]);
  const theme = useTheme();
  const { mainConcernChoices } = getPreChatChoices(t);

  return (
    // Use unmountOnExit to unmount hidden Accordion content for improved performance
    <Box sx={{ marginTop: spacingTop }}>
      <Accordion
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
        slotProps={{ transition: { unmountOnExit: true, timeout: 0 } }}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          expandIcon={
            <RotatableAddIcon
              rotate={expanded ? 0 : 1}
              sx={{ fontSize: "20px", color: theme.palette.white }}
            />
          }
          sx={{
            "&.Mui-expanded": {
              margin: 0,
            },
            ".MuiAccordionSummary-content": {
              margin: 0,
              flexDirection: "column",
            },
            bgcolor: theme.palette.primary.main,
            color: theme.palette.white,
            borderRadius: "4px",
            padding: "10px 16px",
            "&:focus": {
              outline: `1px dashed ${theme.palette.tertiary.main}`,
              outlineOffset: 4,
            },
            "&.MuiAccordionSummary-root": {
              bgcolor: theme.palette.primary.main,
              color: theme.palette.white,
            },
            ":hover": {
              bgcolor: theme.palette.secondary.dark,
            },
          }}
        >
          <Typography
            sx={{
              color: theme.palette.white,
              fontFamily: "Public Sans",
              fontSize: "16px",
              fontWeight: "800",
            }}
          >
            {expanded
              ? t("optionalQuestionHeader.collapseTitle")
              : t("optionalQuestionHeader.expandTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {expanded && <SubText />}
          <SurveyTextField
            label={t("nameAliasQuestion.placeHolder")}
            name="name"
            autoComplete="name"
            title={t("nameAliasQuestion.title")}
            description={t("nameAliasQuestion.infoText")}
            control={control}
            inputProps={{ maxLength: 50 }}
          />

          <SurveyTextField
            label={t("ageQuestion.placeHolder")}
            name="age"
            title={t("ageQuestion.title")}
            description={t("ageQuestion.infoText")}
            control={control}
            maskFormat="###"
            inputProps={{ inputMode: "numeric" }}
          />
          <SurveyPillQuestion
            mainLabelText={t("mainConcernsQuestion.title")}
            subLabelText={t("mainConcernsQuestion.subText")}
            control={control}
            spacingTop={1.5}
            spacingBottom={3}
            options={mainConcernChoices}
            name="mainConcerns"
          />
        </AccordionDetails>
      </Accordion>
      {!expanded && <SubText />}
    </Box>
  );
};

function SubText() {
  const { t } = useTranslation("preChat");
  const theme = useTheme();

  return (
    <Typography
      variant="body1"
      sx={{
        fontStyle: "italic",
        color: theme.palette.secondary.dark,
        mt: 1,
        mb: 3,
      }}
    >
      {t("optionalQuestionHeader.subText")}
    </Typography>
  );
}

SurveyAccordion.propTypes = {
  control: PropTypes.object.isRequired,
  spacingTop: PropTypes.number,
};

export default SurveyAccordion;
